/* RulesHub — single-page hub for all rule surfaces.
 *
 * Tabs:
 *   • Watching — promoted rules on the active project (the watchlist)
 *   • Library   — the catalog (50 rules), multi-select promote
 *   • Drawings  — typed drawing classes + which rules read each
 *
 * Why one page, not three routes: the user explicitly asked for a
 * clear flow. The previous /rules ↔ /library bouncing was the
 * confusion. Now everything is one place — tab into Library to add,
 * tab into Drawings to see the spatial layer that grounds the rules.
 *
 * Tab state is URL-synced via `?tab=` so deep links work and back/
 * forward feels right.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import {
  Plus,
  Wrench,
  X,
  Sparkles,
  Eye,
  BookOpen,
  Hammer,
  Layers,
} from "lucide-react";
import { BuilderTab } from "./BuilderTab";
import { DrawingsHub } from "./DrawingsHub";
import {
  DESIGNER_RULES,
  CATEGORY_LABEL,
  CATEGORY_COLOR,
  type DesignerRule,
} from "../../data/designerRules";
import { predicateToText } from "../../lib/predicateRender";
import {
  listPromoted,
  listCustom,
  demote,
  promoteMany,
  type CustomRule,
} from "../../lib/projectRules";
import { useActiveProjectGraph } from "../../lib/useActiveProjectGraph";
import { cls } from "../../lib/cls";
import { RuleLibrary } from "../library/RuleLibrary";
import { StandardsRow } from "./StandardsRow";

type TabId = "active" | "library" | "builder" | "drawings";

const TABS: Array<{ id: TabId; label: string; icon: JSX.Element; hint: string }> = [
  { id: "active",   label: "Active rules",   icon: <Eye      className="w-3.5 h-3.5" />, hint: "Rules currently watching this project" },
  { id: "library",  label: "Rules library",  icon: <BookOpen className="w-3.5 h-3.5" />, hint: "Browse the 50 typed senior-designer rules" },
  { id: "builder",  label: "Rule builder",   icon: <Hammer   className="w-3.5 h-3.5" />, hint: "Drag-drop ontology · compose a new rule" },
  { id: "drawings", label: "Drawings",       icon: <Layers   className="w-3.5 h-3.5" />, hint: "FloorPlan · Elevation · Section · RCP · MEP · 3D scene — typed surfaces this project's rules read" },
];

function tabFromSearch(search: URLSearchParams): TabId {
  const t = search.get("tab");
  if (t === "library" || t === "builder" || t === "active" || t === "drawings") return t;
  if (t === "watching") return "active"; // legacy
  return "active";
}

function naturalTrigger(rule: DesignerRule | CustomRule): string {
  switch (rule.trigger) {
    case "onSave":             return "Every time the project is saved";
    case "onBOQRowAdded":      return "When a BOQ row is added";
    case "onSkuChanged":       return "When an SKU is added or changed";
    case "onMilestoneSet":     return "When a milestone is set or moved";
    case "onApprovalDecided":  return "When a client approval lands";
    case "onBudgetChanged":    return "When the budget cap or share moves";
    case "onDeadlineChanged":  return "When the handover date changes";
    default:                   return "Every project change";
  }
}

function naturalAction(rule: DesignerRule | CustomRule): string {
  switch (rule.action) {
    case "flag":         return rule.severity === "block" ? "Blocks save · asks you to fix" : "Flags it for your review";
    case "suggest":      return "Suggests a better choice";
    case "openApproval": return "Drafts a client approval packet";
    case "substitute":   return "Proposes a substitute on the spot";
    case "redistribute": return "Suggests a redistribution";
  }
}

interface RulesHubProps {
  /** Override the initial tab from a wrapping route (e.g. /library
   *  mounts RulesHub with `initialTab="library"`). */
  initialTab?: TabId;
}

export function RulesHub({ initialTab }: RulesHubProps): JSX.Element {
  const [search, setSearch] = useSearchParams();
  const { graph, activeId } = useActiveProjectGraph();
  const [tab, setTab] = useState<TabId>(() => initialTab ?? tabFromSearch(search));

  // Keep URL ?tab in sync with state.
  useEffect(() => {
    const cur = search.get("tab");
    if (cur !== tab) {
      const next = new URLSearchParams(search);
      next.set("tab", tab);
      setSearch(next, { replace: true });
    }
  }, [tab, search, setSearch]);

  // Re-read on project switch.
  const [promoted, setPromoted] = useState<string[]>(() => listPromoted(activeId));
  const [custom, setCustom] = useState<CustomRule[]>(() => listCustom(activeId));
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  useEffect(() => {
    setPromoted(listPromoted(activeId));
    setCustom(listCustom(activeId));
    setSelected(new Set());
  }, [activeId]);

  const allKnown = useMemo<Array<DesignerRule | CustomRule>>(
    () => [...DESIGNER_RULES, ...custom],
    [custom],
  );
  const watching = useMemo(
    () => allKnown.filter((r) => promoted.includes(r.id)),
    [allKnown, promoted],
  );

  const onDemote = useCallback((ruleId: string) => {
    setPromoted(demote(activeId, ruleId));
  }, [activeId]);

  const onToggleSelect = useCallback((rule: DesignerRule): void => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(rule.id)) next.delete(rule.id);
      else next.add(rule.id);
      return next;
    });
  }, []);

  const onOpenInBuilder = useCallback((rule: DesignerRule): void => {
    const params = new URLSearchParams(search);
    params.set("tab", "builder");
    params.set("from", rule.id);
    setSearch(params, { replace: false });
    setTab("builder");
  }, [search, setSearch]);

  const onPromoteSelected = useCallback((): void => {
    if (selected.size === 0) return;
    const next = promoteMany(activeId, Array.from(selected));
    setPromoted(next);
    setSelected(new Set());
    setTab("active");
  }, [selected, activeId]);

  const onPromotedFromBuilder = useCallback((): void => {
    setPromoted(listPromoted(activeId));
    setCustom(listCustom(activeId));
    const params = new URLSearchParams(search);
    params.set("tab", "active");
    params.delete("from");
    setSearch(params, { replace: true });
    setTab("active");
  }, [activeId, search, setSearch]);

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-8 sm:py-10 pb-28">
      {/* Header */}
      <header className="mb-5 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="eyebrow">Reasoning rules</div>
          <h1 className="display-2 mt-2 text-ink-950">
            Watching <span className="text-foyr">{graph.projectName}</span>.
          </h1>
          <p className="mt-2 text-ink-700 max-w-3xl">
            One place: what's watching the project · the catalog of 50 typed rules · the ontology drag-drop
            builder · and the typed drawings (FloorPlan, Elevation, Section, RCP, MEP, 3D scene) the rules
            actually read from. Click any rule to open it in the builder.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => { setTab("builder"); }}
            className="btn btn-foyr !py-2 !px-3 !text-[13px]"
            title="Compose a new rule from scratch"
          >
            <Plus className="w-3.5 h-3.5" /> <span>New rule</span>
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="sticky top-2 z-10 mb-5">
        <div className="panel p-2 flex items-center gap-1 flex-wrap">
          {TABS.map((t) => {
            const active = tab === t.id;
            const badge =
              t.id === "active"   ? watching.length :
              t.id === "library"  ? DESIGNER_RULES.length :
              t.id === "drawings" ? 6 : // six typed drawing classes, surfaced via rules
              undefined;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                title={t.hint}
                className={cls(
                  "px-3 py-2 rounded-lg text-[13px] font-medium inline-flex items-center gap-2 transition-all",
                  active
                    ? "bg-ink-950 text-cream"
                    : "text-ink-700 hover:bg-cream-dark",
                )}
              >
                {t.icon}
                <span>{t.label}</span>
                {badge !== undefined ? (
                  <span className={cls(
                    "tabular text-[10px] px-1.5 py-0.5 rounded",
                    active ? "bg-cream/20 text-cream" : "bg-cream-dark text-ink-500",
                  )}>{badge}</span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab body */}
      {tab === "active" ? (
        watching.length === 0 ? (
          <EmptyWatching onBrowse={() => setTab("library")} />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {watching.map((r, i) => (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: i * 0.02 }}
                onClick={() => onOpenInBuilder(r)}
                className="panel p-4 flex flex-col gap-2 cursor-pointer hover:ring-1 hover:ring-ink-300 transition-all"
              >
                <div className="flex items-start justify-between gap-2">
                  <span className={cls("pill border", CATEGORY_COLOR[r.category])}>
                    {CATEGORY_LABEL[r.category]}
                  </span>
                  <div className="flex items-center gap-1">
                    {"author" in r ? (
                      <span className="pill bg-mulberry/10 text-mulberry border border-mulberry/30 tabular text-[10px]">
                        <Sparkles className="w-2.5 h-2.5 inline mr-0.5" />studio rule
                      </span>
                    ) : null}
                    <span className={cls(
                      "pill",
                      r.severity === "block" ? "bg-foyr text-cream" :
                      r.severity === "warn"  ? "bg-foyr/10 text-foyr-deep" :
                                               "bg-cream-dark border border-border text-ink-700",
                    )}>{r.severity}</span>
                  </div>
                </div>
                <div className="text-sm font-semibold text-ink-950 leading-snug">{r.title}</div>
                <div className="text-xs text-ink-700 leading-snug">{r.natural}</div>
                <div className="font-mono text-[11px] text-ink-700 leading-snug bg-cream-deeper rounded-lg px-2 py-1.5">
                  {predicateToText(r.predicate)}
                </div>
                <div className="grid grid-cols-2 gap-1 text-[10px] text-ink-500">
                  <span><span className="uppercase tracking-wider">when</span> <span className="text-ink-700">{naturalTrigger(r)}</span></span>
                  <span><span className="uppercase tracking-wider">→</span> <span className="text-ink-700">{naturalAction(r)}</span></span>
                </div>
                {r.dataset ? (
                  <div className="text-[10px] font-mono text-ink-500/80">↳ {r.dataset}</div>
                ) : null}
                <div className="flex items-center justify-between mt-1 gap-2">
                  <span className="text-[11px] text-ink-500 inline-flex items-center gap-1">
                    <Wrench className="w-3 h-3" /> Click anywhere to tune
                  </span>
                  <button
                    type="button"
                    onClick={(ev) => { ev.stopPropagation(); onDemote(r.id); }}
                    className="text-[11px] text-ink-500 hover:text-foyr inline-flex items-center gap-1"
                  >
                    <X className="w-3 h-3" /> Stop watching
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )
      ) : null}

      {tab === "library" ? (
        <>
          <div className="mb-3"><StandardsRow /></div>
          <RuleLibrary
            promotedIds={new Set(promoted)}
            selectedIds={selected}
            onToggleSelect={onToggleSelect}
            onOpenInBuilder={onOpenInBuilder}
          />
          {selected.size > 0 ? (
            <div className="fixed bottom-0 left-0 right-0 bg-cream/85 backdrop-blur border-t border-border z-20 px-4 sm:px-8 py-3">
              <div className="max-w-[1400px] mx-auto flex items-center justify-between gap-3">
                <div className="text-[13px] text-ink-700">
                  <span className="font-semibold text-ink-950">{selected.size}</span>{" "}
                  rule{selected.size === 1 ? "" : "s"} selected · ready to promote to{" "}
                  <span className="font-mono text-ink-950">{graph.projectName}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSelected(new Set())}
                    className="btn btn-outline !py-2 !px-3 !text-[13px]"
                  >Clear</button>
                  <button
                    type="button"
                    onClick={onPromoteSelected}
                    className="btn btn-foyr !py-2 !px-4 !text-[13px]"
                  >Promote {selected.size} to {graph.projectName}</button>
                </div>
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      {tab === "builder"  ? <BuilderTab onPromoted={onPromotedFromBuilder} /> : null}
      {tab === "drawings" ? <DrawingsHub /> : null}
    </div>
  );
}

function EmptyWatching({ onBrowse }: { onBrowse: () => void }): JSX.Element {
  return (
    <div className="panel p-8 text-center max-w-2xl mx-auto">
      <span className="w-12 h-12 rounded-2xl bg-ink-950 text-cream grid place-items-center mx-auto mb-4">
        <Eye className="w-5 h-5" />
      </span>
      <h2 className="text-lg font-semibold text-ink-950">Nothing watching this project yet.</h2>
      <p className="text-[14px] text-ink-700 mt-2 mb-5">
        Open the Rules library tab to promote a few of the 50 typed rules, or compose your own in the builder.
      </p>
      <div className="flex items-center justify-center gap-2">
        <button onClick={onBrowse} className="btn btn-outline !py-2 !px-3 !text-[13px]">
          <BookOpen className="w-3.5 h-3.5" /> <span>Browse library</span>
        </button>
      </div>
    </div>
  );
}
